import axios from 'axios'
import {
  CommandHistory,
  CommandHistoryResponse,
  DeviceCatalog,
  MomiStatus,
  SendCommandPayload,
  SendCommandResponse,
} from '../types/commandBus.types'

const BACKEND_URL = process.env.REACT_APP_BACKEND_API_URL + '/api/v1'

// Erreur dédiée pour le cas 409 "momi_offline" — permet à la couche UI de
// distinguer un MoMi déconnecté d'une vraie erreur backend, et d'afficher
// un message ciblé en français sans inspecter response.data côté composant.
export class MomiOfflineError extends Error {
  constructor(message = 'Momi hors ligne') {
    super(message)
    this.name = 'MomiOfflineError'
  }
}

// Erreur dédiée pour le cas 404 sur le catalogue : le device n'a jamais
// été provisionné via un catalog_push WS, donc on n'a aucun verbe à afficher.
// La page consommatrice masque alors le panel et affiche une info text.
export class DeviceNotProvisionedError extends Error {
  constructor(message = 'Device non provisionné dans le command bus') {
    super(message)
    this.name = 'DeviceNotProvisionedError'
  }
}

export const sendCommand = async (
  devEui: string,
  commandName: string,
  params: Record<string, unknown>,
): Promise<SendCommandResponse> => {
  const payload: SendCommandPayload = {
    dev_eui: devEui,
    command_name: commandName,
    params,
  }
  try {
    const res = await axios.post<SendCommandResponse>(
      `${BACKEND_URL}/commands`,
      payload,
    )
    return res.data
  } catch (err: any) {
    if (err?.response?.status === 409) {
      throw new MomiOfflineError(
        err.response?.data?.error === 'momi_offline'
          ? 'Momi hors ligne'
          : err.response?.data?.error ?? 'Momi hors ligne',
      )
    }
    throw err
  }
}

export const getCommandHistory = async (
  devEui: string,
  page: number,
  pageSize: number,
): Promise<CommandHistoryResponse> => {
  const params = new URLSearchParams({
    dev_eui: devEui,
    page: page.toString(),
    pageSize: pageSize.toString(),
  })
  const res = await axios.get<CommandHistoryResponse>(
    `${BACKEND_URL}/commands?${params.toString()}`,
  )
  return res.data
}

export const getCommand = async (commandId: string): Promise<CommandHistory> => {
  const res = await axios.get<CommandHistory>(
    `${BACKEND_URL}/commands/${commandId}`,
  )
  return res.data
}

export const getDeviceCatalog = async (devEui: string): Promise<DeviceCatalog> => {
  try {
    const res = await axios.get<DeviceCatalog>(
      `${BACKEND_URL}/commands/catalog/${devEui}`,
    )
    return res.data
  } catch (err: any) {
    if (err?.response?.status === 404) {
      throw new DeviceNotProvisionedError()
    }
    throw err
  }
}

export const getMomiStatus = async (momiId: string): Promise<MomiStatus> => {
  const res = await axios.get<MomiStatus>(
    `${BACKEND_URL}/momis/${momiId}/status`,
  )
  return res.data
}

const commandBusAPI = {
  sendCommand,
  getCommandHistory,
  getCommand,
  getDeviceCatalog,
  getMomiStatus,
}

export default commandBusAPI
